import { type LocationQueryValue, type Router } from 'vue-router';
import { PUBLIC_ROUTES } from '@/modules/Login/constants/login.constant';
import PermissionService from '@/utils/PermissionService';

type NextParam = LocationQueryValue | LocationQueryValue[] | undefined;

/**
 * The login page for someone who asked for `fullPath`, carrying it as
 * `?next=` so that signing in takes them there rather than to the start
 * page. The start page itself is not worth carrying.
 */
export function loginRoute(fullPath?: string) {
  if (!fullPath || fullPath === '/') return { name: 'login' };
  return { name: 'login', query: { next: fullPath } };
}

/**
 * Where to go after signing in, read from a `?next=` value, or `null` to
 * fall back to the start page.
 *
 * Only a path inside the Map Builder is honoured: it must start with a
 * single slash (`//host` is a URL, not a path) and resolve to a named
 * route that needs a session -- which also keeps `?next=/login` from
 * sending the user straight back to the form.
 */
export function returnAddress(router: Router, next: NextParam): string | null {
  if (typeof next !== 'string' || !/^\/(?![/\\])/.test(next)) return null;

  const resolved = router.resolve(next);
  const name = resolved.name;
  if (!resolved.matched.length || typeof name !== 'string') return null;
  if (PUBLIC_ROUTES.includes(name)) return null;

  return resolved.fullPath;
}

/**
 * Whether the user may open `path`, judged by its route's `permission`.
 *
 * Which client a page belongs to is not known before it loads -- the Editor
 * learns it from the map's owner -- so a role in any of the user's clients
 * is enough. This only spares a user the page they cannot use; the API
 * refuses them regardless.
 */
export function mayOpen(
  router: Router,
  path: string,
  userRoles: Record<string, string>,
  superuser = false
): boolean {
  const permission = router.resolve(path).meta?.permission;
  if (!permission || superuser) return true;
  return Object.values(userRoles).some(
    (userRole) => PermissionService.permissionCheck({ userRole, permission })
  );
}
